import { z } from "zod";
import {
	prepareX4MachineData,
	type MachineCorrugateTrack,
	type X4MachineRequest,
	type X4Machine,
	x4MachineSchema,
	getX4ChangeCorrugatePayloadSchema,
} from "@packsize/schemas/machines";

import { packNetAxios } from "@/lib/api-client";

import { baseRoutes } from "../base-routes";

const x4Api = `${baseRoutes.x4}/api/v1`;

export async function createX4Machine(data: X4MachineRequest): Promise<void> {
	await packNetAxios.post(x4Api, data);
}

export async function updateX4Machine(tenantId: string, machineId: string, data: X4MachineRequest): Promise<void> {
	// TODO @eb: Remove machineId and tenantId from the payload when the backend no longer requires it.
	await packNetAxios.put(`${x4Api}/${machineId}`, {
		id: machineId,
		tenantId,
		...data,
	});
}

export async function getX4Machines(): Promise<X4Machine[]> {
	const response = await packNetAxios.get(x4Api);

	if (Array.isArray(response.data) === false) {
		throw new Error("Unable to fetch X4 machines, response is not a list");
	}

	return z
		.array(x4MachineSchema)
		.enrich({ callSite: "getX4Machines - v2" })
		.parseWithTelemetry(response.data.map(prepareX4MachineData));
}

export async function getX4Machine(machineId: string): Promise<X4Machine | null> {
	const response = await packNetAxios.get(`${x4Api}/machines/${machineId}`);

	if (response.status !== 200) {
		return null;
	}

	return x4MachineSchema
		.enrich({ callSite: "getX4Machine - v2" })
		.parseWithTelemetry(prepareX4MachineData(response.data));
}

export async function updateX4Tracks(
	machineId: string,
	tenantId: string,
	tracks: MachineCorrugateTrack[],
	tenantUsesInches: boolean,
): Promise<void> {
	const payload = getX4ChangeCorrugatePayloadSchema(tenantUsesInches).parse(tracks);

	await packNetAxios.put(
		`${baseRoutes.x4}/api/v1.0/tenants/${tenantId}/machines/${machineId}/configuretracks`,
		payload,
	);
}

export async function pauseX4Machine(machineId: string, tenantId: string): Promise<void> {
	await packNetAxios.post(`${x4Api}/tenants/${tenantId}/machines/${machineId}/stateChange/3`, null);
}
